import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text } = Typography;

const TaskManagerProjectsPage = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await tmApi.get(
                '/api/task-manager/projects'
            );
            setRows(data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки проектов'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            sort_order: 100,
            is_active: true,
            color: '#2f6fed'
        });
        setOpen(true);
    };

    const openEdit = record => {
        setEditing(record);
        form.setFieldsValue(record);
        setOpen(true);
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            if (editing) {
                await tmApi.put(
                    `/api/task-manager/projects/${editing.id}`,
                    values
                );
                message.success('Проект обновлён');
            } else {
                await tmApi.post(
                    '/api/task-manager/projects',
                    values
                );
                message.success('Проект создан');
            }
            setOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const remove = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/projects/${id}`
            );
            message.success('Проект удалён');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const columns = useMemo(
        () => [
            {
                title: 'Цвет',
                dataIndex: 'color',
                width: 70,
                render: c => (
                    <span
                        style={{
                            display: 'inline-block',
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            background: c || '#ccc'
                        }}
                    />
                )
            },
            {
                title: 'System',
                dataIndex: 'system_name',
                width: 180
            },
            {
                title: 'Название',
                dataIndex: 'human_name'
            },
            {
                title: 'Описание',
                dataIndex: 'description',
                ellipsis: true
            },
            {
                title: 'Порядок',
                dataIndex: 'sort_order',
                width: 90
            },
            {
                title: 'Активен',
                dataIndex: 'is_active',
                width: 90,
                render: v =>
                    v ? (
                        <Tag color="green">да</Tag>
                    ) : (
                        <Tag>нет</Tag>
                    )
            },
            {
                title: '',
                width: 110,
                render: (_, row) => (
                    <Space>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEdit(row)}
                        />
                        <Popconfirm
                            title="Удалить проект?"
                            onConfirm={() => remove(row.id)}
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Space>
                )
            }
        ],
        []
    );

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Проекты
                        </Title>
                        <Text type="secondary">
                            Стройка, обслуживание станций и другие
                            направления
                        </Text>
                    </div>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                        >
                            Новый проект
                        </Button>
                    </Space>
                </Space>

                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={rows}
                    columns={columns}
                    pagination={false}
                />

                <Modal
                    open={open}
                    title={
                        editing
                            ? `Проект #${editing.id}`
                            : 'Новый проект'
                    }
                    onCancel={() => setOpen(false)}
                    onOk={save}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="system_name"
                            label="System name"
                            rules={[{ required: true }]}
                        >
                            <Input
                                disabled={Boolean(editing)}
                                placeholder="station_maintenance"
                            />
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Обслуживание станций" />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>
                        <Form.Item name="color" label="Цвет">
                            <Input type="color" />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активен"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerProjectsPage;
